import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { captureSignupUtmFromUrl, getStoredUtm, withSignupUtm } from '../utils/signupUtm';
import {
  identifyCustomerIoSignupProspect,
  trackCustomerIoFormStarted,
  trackCustomerIoFormSubmitted,
  trackCustomerIoSignupFailed,
} from '../utils/customerio';
import blackbutton from '../assets/images/BTN_Large_Lightbutton.svg';
import loadingSpinner from '../assets/images/3-dots-scale-middle.svg';
import BuildingShimmerText from '../components/BuildingShimmerText';
import {
  functions,
  setGlobalState,
  useGlobalState,
} from '../components/AppContext';
import '../assets/css/Signup.css';
import { httpsCallable } from 'firebase/functions';
import { ref, set, serverTimestamp } from 'firebase/database';
import { database } from '../firebase';
import {
  getSiteNameConflictUserMessage,
  isAmbiguousSignupClientFailure,
  stripBilditPrefixForDisplay,
} from '../utils/callableErrors';
import { buildBilditSiteName } from '../utils/localDevSignup';
import { useSignupKeepPageOpen } from '../hooks/useSignupKeepPageOpen';
import { SIGNUP_PHASE1_KEEP_OPEN_MESSAGE, SIGNUP_PROVISIONED_MESSAGE } from '../utils/signupMessaging';

function SignupAIPixel() {
  const [emailAddress] = useGlobalState('email');
  const [siteName] = useGlobalState('siteName');
  const [siteUrl] = useGlobalState('siteUrl');
  const [siteCreatedStatus, setSiteCreatedStatus] = useState();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    captureSignupUtmFromUrl();
  }, []);

  useSignupKeepPageOpen(loading && !siteCreatedStatus);

  const createTenant = httpsCallable(functions, 'createTenant', {
    timeout: 300000,
  });
  const sendErrorEmail = httpsCallable(functions, 'sendErrorEmail');

  const handleSiteNameChange = (e) => {
    setFormError('');
    setGlobalState('siteName', e.target.value);

    // Track form interaction
    if (typeof gtag !== 'undefined' && e.target.value.length === 1) {
      gtag('event', 'form_start', {
        event_category: 'engagement',
        event_label: 'signup_form_started',
      });
    }

    e.preventDefault();
  };

  const handleSiteUrlChange = (e) => {
    setFormError('');
    setGlobalState('siteUrl', e.target.value);
    e.preventDefault();
  };

  const handleEmailChange = (e) => {
    setFormError('');
    setGlobalState('email', e.target.value);
    trackCustomerIoFormStarted(
      {
        signup_source: 'aipixel',
        plan_type: 'free_ai_analytics',
        event_label: 'signup_form_started',
      },
      e.target.value
    );
    e.preventDefault();
  };

  const logError = async (siteKey, errorDetails, urlForLog) => {
    try {
      const errorRef = ref(database, `sites/${siteKey}/errors`);
      await set(errorRef, {
        timestamp: serverTimestamp(),
        error: errorDetails.message || 'Unknown error during signup',
        code: errorDetails.code,
        email: emailAddress,
        siteUrl: urlForLog,
        context: 'free-plan-signup',
      });
      // eslint-disable-next-line no-empty
    } catch { }
  };

  const setupFreePlanForCustomer = async () => {
    // Track form submission attempt
    if (typeof gtag !== 'undefined') {
      gtag('event', 'form_submit', {
        event_category: 'engagement',
        event_label: 'signup_form_submitted',
      });
    }
    trackCustomerIoFormStarted(
      {
        signup_source: 'aipixel',
        plan_type: 'free_ai_analytics',
        event_label: 'signup_form_started',
      },
      emailAddress
    );
    trackCustomerIoFormSubmitted(
      {
        signup_source: 'aipixel',
        plan_type: 'free_ai_analytics',
        event_label: 'signup_form_submitted',
      },
      emailAddress
    );

    setFormError('');
    setLoading(true);
    const trimmedSiteName = siteName ? siteName.trim() : '';
    const trimmedSiteUrl = siteUrl ? siteUrl.trim() : '';
    const trimmedEmail = emailAddress ? emailAddress.trim() : '';

    setGlobalState('email', trimmedEmail);
    setGlobalState('siteUrl', trimmedSiteUrl);

    if (!trimmedSiteName) {
      alert('Please enter a site name.');
      setLoading(false);
      return;
    }

    if (!trimmedSiteUrl) {
      alert('Please enter your website URL.');
      setLoading(false);
      return;
    }

    if (!trimmedEmail) {
      alert('Please enter an email address.');
      setLoading(false);
      return;
    }

    let siteKeyForLogging = trimmedSiteName;

    try {
      const siteNameStripped = buildBilditSiteName(trimmedSiteName);
      setGlobalState('siteName', siteNameStripped);
      siteKeyForLogging = siteNameStripped;

      const createTenantResult = await createTenant({
        siteName: siteNameStripped,
        email: trimmedEmail,
        siteUrl: trimmedSiteUrl,
        signupSource: 'aipixel',
        utm: getStoredUtm(),
      });

      if (!createTenantResult || createTenantResult.data?.code !== 200) {
        const errorDetails = createTenantResult?.data || {
          message: 'Unknown error creating tenant.',
        };
        await logError(siteNameStripped, errorDetails, trimmedSiteUrl);
        try {
          await sendErrorEmail({
            siteName: siteNameStripped,
            userEmail: trimmedEmail,
            errorDetails,
          });
          // eslint-disable-next-line no-empty
        } catch { }
        trackCustomerIoSignupFailed(
          {
            failure_stage: 'tenant_creation',
            error_reason: errorDetails.message || 'tenant_creation_failed',
            error_code: errorDetails.code,
            signup_source: 'aipixel',
            plan_type: 'free_ai_analytics',
          },
          trimmedEmail
        );
        alert('There was an issue creating your site. Please try again later.');
        setLoading(false);
        setSiteCreatedStatus(false);
        return;
      }

      setFormError('');
      setSiteCreatedStatus(true);
      identifyCustomerIoSignupProspect(trimmedEmail, {
        site_name: siteNameStripped,
        website_url: trimmedSiteUrl,
        plan_type: 'free_ai_analytics',
        signup_source: 'aipixel',
      });

      // Track successful signup conversion
      if (typeof gtag !== 'undefined') {
        // Primary conversion event
        gtag(
          'event',
          'conversion',
          withSignupUtm({
            event_category: 'signup',
            event_label: 'free_ai_analytics_plan',
            value: 1,
            currency: 'USD',
            custom_parameter_1: siteNameStripped,
            custom_parameter_2: trimmedSiteUrl,
          })
        );

        // Standard GA4 sign_up event
        gtag(
          'event',
          'sign_up',
          withSignupUtm({
            method: 'email',
            event_category: 'engagement',
            user_type: 'new_user',
            plan_type: 'free_ai_analytics',
          })
        );

        // Purchase event (for free plan conversion)
        gtag(
          'event',
          'purchase',
          withSignupUtm({
            transaction_id: siteNameStripped + '_' + Date.now(),
            value: 0,
            currency: 'USD',
            items: [
              {
                item_id: 'free_ai_analytics_plan',
                item_name: 'Free AI Analytics Tool',
                category: 'subscription',
                quantity: 1,
                price: 0,
              },
            ],
          })
        );

        // Generate lead event
        gtag(
          'event',
          'generate_lead',
          withSignupUtm({
            currency: 'USD',
            value: 0,
            lead_type: 'free_signup',
          })
        );
      }
    } catch (err) {
      const conflictMessage = getSiteNameConflictUserMessage(err);
      if (conflictMessage) {
        setFormError(conflictMessage);
        setGlobalState('siteName', stripBilditPrefixForDisplay(siteKeyForLogging));
        setSiteCreatedStatus(false);
        trackCustomerIoSignupFailed(
          {
            failure_stage: 'tenant_creation',
            error_reason: 'site_name_conflict',
            signup_source: 'aipixel',
            plan_type: 'free_ai_analytics',
          },
          trimmedEmail
        );
        return;
      }
      const fallbackSiteKey =
        siteKeyForLogging || trimmedSiteName.replace(/[^a-zA-Z0-9]/g, '');
      await logError(
        fallbackSiteKey,
        { message: err.message, code: err.code },
        trimmedSiteUrl
      );
      if (!isAmbiguousSignupClientFailure(err)) {
        trackCustomerIoSignupFailed(
          {
            failure_stage: 'unexpected',
            error_reason: err.message || 'unexpected_error',
            signup_source: 'aipixel',
            plan_type: 'free_ai_analytics',
          },
          trimmedEmail
        );
      }
      alert('An unexpected error occurred. Please try again later.');
      setSiteCreatedStatus(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-page-container">
      <div className="landing-page-content">
        <div className="left-section">
          <h1 className="landing-title">Claim Your Free AI Analytics Tool</h1>
          <h3 className="landing-subtitle">
            Includes AIO/GEO Bot Analytics Signals Reporting
          </h3>

          <div className="features-list">
            <div className="feature-item">
              <div className="feature-bullet">•</div>
              <div className="feature-text">
                Advanced AI-powered analytics to track user behavior and
                engagement patterns
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-bullet">•</div>
              <div className="feature-text">
                Real-time reporting with actionable insights like errors,
                clicks, browses, and crawls to optimize your website experience
                for AI bots
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-bullet">•</div>
              <div className="feature-text">
                Seamless integration with the BILDIT Visual Experience Engine -
                no technical setup required
              </div>
            </div>
          </div>
        </div>

        <div className="right-section">
          {!siteCreatedStatus && (
            <div className="form-container">
              <div className="form-box">
                {!loading && (
                  <div className="signup-form">
                    <span className="form-subtitle">
                      Start using the AI Analytics Tool inside the BILDIT Visual
                      Experience Engine for free
                    </span>
                    <input
                      type="text"
                      placeholder="Site Name"
                      className="form-input"
                      name="Site-Name"
                      onChange={handleSiteNameChange}
                      value={siteName}
                    />
                    <input
                      type="text"
                      placeholder="Website URL"
                      className="form-input"
                      name="Site-Url"
                      onChange={handleSiteUrlChange}
                      value={siteUrl}
                    />
                    <input
                      type="text"
                      placeholder="Email Address"
                      className="form-input"
                      name="Email-Capture"
                      onChange={handleEmailChange}
                      value={emailAddress}
                    />
                    {formError ? (
                      <p className="signup-inline-error" role="alert">
                        {formError}
                      </p>
                    ) : null}
                    <Link>
                      <img
                        src={blackbutton}
                        className="submit-button"
                        onClick={setupFreePlanForCustomer}
                        alt="Submit"
                      />
                    </Link>
                    <p className="disclaimer-text">
                      No credit card required – this plan is always free.
                    </p>
                    <p className="signup-recovery-nav">
                      <Link
                        to="/account-recovery"
                        className="signup-recovery-link"
                      >
                        Already have an account? Login
                      </Link>
                    </p>
                  </div>
                )}
                {loading && (
                  <div className="loading-container">
                    <div className="loading-spinner">
                      <img src={loadingSpinner} alt="Loading" />
                    </div>
                    <div className="loading-copy">
                      <p className="loading-title">
                        <BuildingShimmerText />
                      </p>
                      <p className="loading-detail">
                        {SIGNUP_PHASE1_KEEP_OPEN_MESSAGE}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {siteCreatedStatus && (
            <div role="status">
              <p>{SIGNUP_PROVISIONED_MESSAGE}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SignupAIPixel;
